/**
 * @file コンポーネント：フル動画ページフル動画プレイヤー ロジック管理
 */

// ライブラリ読み込み
import React, { useEffect, useRef, useState } from "react";

// Presenter読み込み
import { Presenter } from "./presenter";

// 関数読み込み
import {
    fnConvertSecondToTimeString,
    fnPushDataLayerGtm,
} from "~/src/functions/common";
import { fnSendEventHubspot } from "~/src/functions/hubspot";

// 型定義
interface Topic {
    listTopic: [
        {
            text: string;
            time: string;
            userId: number;
            userName: string;
        }
    ];
    text: string;
}

// コンポーネントを返す
export const FullvideoPlayer = (props) => {
    /**
     * フル動画プレイヤー再生状態による処理
     */
    const [isPlayed, setIsPlayed] = useState(false); // 初回再生済みかどうか
    // 初回再生時に初回再生状態をtrueに
    useEffect(() => {
        if (props.isPlay === true && isPlayed === false) {
            setIsPlayed(true);
        }
    }, [props.isPlay]);
    // 初回再生時の処理
    useEffect(() => {
        if (isPlayed === true) {
            // HubSpotにイベント送信
            fnSendEventHubspot({
                name: "pe21169348_play",
                properties: {
                    state: props.isPlay ? "play" : "pause",
                    time: props.timeProgress,
                },
            });
            // Google Tag Manager カスタムイベントトリガー
            //// スポンサーコンテンツのフル動画が再生された
            if (props.isSponsorContent) {
                fnPushDataLayerGtm({
                    event: "PlayCountFullVideo",
                    operationId: window.atob(props.operationId),
                    userId: window.dataUser.userId,
                });
            }
            //// フル動画が再生された
            fnPushDataLayerGtm({
                event: "PlayFull",
                operationId: window.atob(props.operationId),
                userId: window.dataUser.userId,
            });
        }
    }, [isPlayed]);
    // 再生進捗に応じた処理
    const [isSentProgress25, setIsSentProgress25] = useState(false); // フル動画が全体の25%地点を過ぎたことがあるかどうか
    const [isSentProgress50, setIsSentProgress50] = useState(false); // フル動画が全体の50%地点を過ぎたことがあるかどうか
    const [isSentProgress75, setIsSentProgress75] = useState(false); // フル動画が全体の75%地点を過ぎたことがあるかどうか
    useEffect(() => {
        if (
            props.isPlay === true &&
            props.timeProgressSecond !== 0 &&
            props.timeTotalSecond !== 0
        ) {
            // Google Tag Manager カスタムイベントトリガー
            //// フル動画が全体の25%地点を過ぎた
            if (
                props.timeProgressSecond >= props.timeTotalSecond / 4 &&
                isSentProgress25 === false
            ) {
                fnPushDataLayerGtm({
                    event: "ProgressFull25",
                    operationId: window.atob(props.operationId),
                    userId: window.dataUser.userId,
                    video_parcent: 25,
                    video_progress: fnConvertSecondToTimeString(
                        Math.ceil(props.timeTotalSecond / 4)
                    ),
                });
                setIsSentProgress25(true);
                props.setPlayEvent(props.videoPlayRatioType.Ratio25);
            }
            //// フル動画が全体の50%地点を過ぎた
            if (
                props.timeProgressSecond >= props.timeTotalSecond / 2 &&
                isSentProgress50 === false
            ) {
                fnPushDataLayerGtm({
                    event: "ProgressFull50",
                    operationId: window.atob(props.operationId),
                    userId: window.dataUser.userId,
                    video_parcent: 50,
                    video_progress: fnConvertSecondToTimeString(
                        Math.ceil(props.timeTotalSecond / 2)
                    ),
                });
                setIsSentProgress50(true);
                props.setPlayEvent(props.videoPlayRatioType.Ratio50);
            }
            //// フル動画が全体の75%地点を過ぎた
            if (
                props.timeProgressSecond >= props.timeTotalSecond * 0.75 &&
                isSentProgress75 === false
            ) {
                fnPushDataLayerGtm({
                    event: "ProgressFull75",
                    operationId: window.atob(props.operationId),
                    userId: window.dataUser.userId,
                    video_parcent: 75,
                    video_progress: fnConvertSecondToTimeString(
                        Math.ceil(props.timeTotalSecond * 0.75)
                    ),
                });
                setIsSentProgress75(true);
                props.setPlayEvent(props.videoPlayRatioType.Ratio75);
            }
        }
    }, [
        props.isPlay,
        props.timeProgressSecond,
        props.timeTotalSecond,
        isSentProgress25,
        isSentProgress50,
        isSentProgress75,
    ]);

    /**
     * コントローラー関連
     * ------------------------------------------------------------
     */

    /**
     * 字幕
     */
    const [countSubtitlelist, setCountSubtitlelist] = useState(0); // 字幕リストへの字幕セット数
    const [isShowSubtitle, setIsShowSubtitle] = useState(false); // 字幕表示状態
    const [subtitlelist, setSubtitlelist] = useState([]); // 字幕リスト
    // コメントリストから字幕を追加
    useEffect(() => {
        if (props.timeProgress !== null && props.listComment !== null) {
            props.listComment.forEach((comment) => {
                // 再生時間がコメント時間に一致したら字幕リストに追加
                if (props.timeProgress === comment.time) {
                    const tempSubtitlelist = subtitlelist;
                    tempSubtitlelist.push({
                        text: comment.text,
                        title: null,
                        userId: comment.userId,
                        userName: comment.userName,
                    });
                    setSubtitlelist(tempSubtitlelist);
                    setCountSubtitlelist(countSubtitlelist + 1);
                }
            });
        }
    }, [props.listComment, props.timeProgress]);
    // トピックリストから字幕を追加
    useEffect(() => {
        if (props.timeProgress !== null && props.listTopic !== null) {
            props.listTopic.forEach((topic: Topic) => {
                const topicTitle = topic.text;
                // 再生時間がコメント時間に一致したら字幕リストに追加
                topic.listTopic.forEach((comment) => {
                    if (props.timeProgress === comment.time) {
                        const tempSubtitlelist = subtitlelist;
                        tempSubtitlelist.push({
                            text: comment.text,
                            title: topicTitle,
                            userId: comment.userId,
                            userName: comment.userName,
                        });
                        setSubtitlelist(tempSubtitlelist);
                        setCountSubtitlelist(countSubtitlelist + 1);
                    }
                });
            });
        }
    }, [props.listTopic, props.timeProgress]);

    /**
     * アイコンリスト
     */
    const [isHoverFullvideoContainer, setIsHoverFullvideoContainer] = // フル動画プレイヤーコンテナホバー状態
        useState(false);
    const [isShowIconComment, setIsShowIconComment] = useState(true); // コメント表示状態
    const [isShowIconEvent, setIsShowIconEvent] = useState(false); // イベント表示状態
    const [isShowIconTopic, setIsShowIconTopic] = useState(true); // トピック表示状態
    const refFullvideoPlayerContainer = useRef(null); // フル動画プレイヤーコンテナ

    /**
     * サムネイル
     */
    const [thumbUrl, setThumbUrl] = useState<string | null>(null);
    useEffect(() => {
        async function fetchThumbnail() {
            // 未ログインならサムネイルは表示しない
            if (window.dataUser.isLogin === false) return;
            try {
                console.log(`fetchThumbnail 001`);
                const res = await fetch(`/api/operations/thumbnails/image/video/${props.operationId}`);
                if (!res.ok) return;

                const data = await res.json(); // { url: "https://..." }
                setThumbUrl(data.url);
                console.log(`fetchThumbnail 002`);
            } catch (err) {
                console.error("Failed to load thumbnail", err);
            }
        }

        fetchThumbnail();
    }, []);

    // Presenterに渡す
    return (
        <Presenter
            {...props}
            countSubtitlelist={countSubtitlelist}
            isHoverFullvideoContainer={isHoverFullvideoContainer}
            isPlayed={isPlayed}
            isShowIconComment={isShowIconComment}
            isShowIconEvent={isShowIconEvent}
            isShowIconTopic={isShowIconTopic}
            isShowSubtitle={isShowSubtitle}
            refFullvideoPlayerContainer={refFullvideoPlayerContainer}
            setIsHoverFullvideoContainer={setIsHoverFullvideoContainer}
            setIsShowIconComment={setIsShowIconComment}
            setIsShowIconEvent={setIsShowIconEvent}
            setIsShowIconTopic={setIsShowIconTopic}
            setIsShowSubtitle={setIsShowSubtitle}
            setSubtitlelist={setSubtitlelist}
            subtitlelist={subtitlelist}
            thumbUrl={thumbUrl}
        />
    );
};
